import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "fileInput",
    "dropZone",
    "fileList",
    "previewContainer",
    "previewList",
    "uploadButton",
    "progressContainer",
    "progressBar",
    "progressText",
    "errorContainer",
    "errorMessage"
  ]

  static values = {
    applicationId: String,
    documentType: { type: String, default: "bank_statement" },
    maxFiles: { type: Number, default: 10 },
    maxFileSize: { type: Number, default: 10485760 },
    maxTotalSize: { type: Number, default: 31457280 }
  }

  connect() {
    this.selectedFiles = []
  }

  selectDocumentType(event) {
    if (event.target.type === "radio") {
      this.documentTypeValue = event.target.value

      // Update the file input's accept attribute to match the new document type
      if (this.hasFileInputTarget) {
        if (this.documentTypeValue === "bank_statement") {
          this.fileInputTarget.accept = "application/pdf,.pdf"
        } else {
          this.fileInputTarget.accept = ".pdf,.png,.jpg,.jpeg,.heic,.heif,application/pdf,image/png,image/jpeg,image/heic,image/heif"
        }

        // Clear any previously selected files and hide the preview
        this.fileInputTarget.value = ""
        this.selectedFiles = []
        this.hidePreview()
        this.hideError()
      }
    }
  }

  openFilePicker(event) {
    event.preventDefault()
    event.stopPropagation()
    this.fileInputTarget.click()
  }

  // Handle file selection from file input
  handleFileSelect(event) {
    const files = Array.from(event.target.files)
    if (files.length === 0) return
    this.processFiles(files)
  }

  // Handle drag over event
  handleDragOver(event) {
    event.preventDefault()
    event.stopPropagation()
    this.dropZoneTarget.classList.add("border-blue-500", "bg-blue-50")
  }

  // Handle drag leave event
  handleDragLeave(event) {
    event.preventDefault()
    event.stopPropagation()
    this.dropZoneTarget.classList.remove("border-blue-500", "bg-blue-50")
  }

  // Handle file drop
  handleDrop(event) {
    event.preventDefault()
    event.stopPropagation()
    this.dropZoneTarget.classList.remove("border-blue-500", "bg-blue-50")

    const files = Array.from(event.dataTransfer.files)
    this.processFiles(files)
  }

  // Process and validate files
  processFiles(files) {
    // Hide any previous errors
    this.hideError()

    // Validate files
    const validation = this.validateFiles(files)
    if (!validation.valid) {
      this.showError(validation.message)
      return
    }

    // Store files and show preview
    this.selectedFiles = files
    this.showPreview()
  }

  // Returns the allowed MIME types for the current document type
  allowedTypesForDocumentType() {
    if (this.documentTypeValue === "bank_statement") {
      return ["application/pdf"]
    }

    return [
      "application/pdf",
      "image/png",
      "image/jpeg",
      "image/jpg",
      "image/heic",
      "image/heif"
    ]
  }

  // Validate file selection
  validateFiles(files) {
    const allowedTypes = this.allowedTypesForDocumentType()

    // Check file count
    if (files.length === 0) {
      return { valid: false, message: "Please select at least one file" }
    }

    if (files.length > this.maxFilesValue) {
      return {
        valid: false,
        message: `You can upload a maximum of ${this.maxFilesValue} files at once`
      }
    }

    // Check file types
    for (const file of files) {
      if (!allowedTypes.includes(file.type)) {
        const message = this.documentTypeValue === "bank_statement"
          ? "Please upload your bank statement as a PDF file. Image files are not accepted."
          : `Invalid file type: ${file.name}. Only PDF, PNG, JPEG, and HEIC files are allowed`
        return { valid: false, message }
      }
    }

    // Check individual file sizes
    for (const file of files) {
      if (file.size > this.maxFileSizeValue) {
        return {
          valid: false,
          message: `File too large: ${file.name}. Maximum file size is ${this.formatBytes(this.maxFileSizeValue)}`
        }
      }
    }

    // Check total size
    const totalSize = files.reduce((sum, file) => sum + file.size, 0)
    if (totalSize > this.maxTotalSizeValue) {
      return {
        valid: false,
        message: `Total file size exceeds limit. Maximum total size is ${this.formatBytes(this.maxTotalSizeValue)}`
      }
    }

    return { valid: true }
  }

  // Show preview of selected files
  showPreview() {
    if (!this.hasPreviewContainerTarget || !this.hasPreviewListTarget) return

    // Clear existing preview
    this.previewListTarget.innerHTML = ""

    // Build preview list
    this.selectedFiles.forEach((file, index) => {
      const fileItem = document.createElement("div")
      fileItem.className = "flex items-center justify-between p-3 bg-[#E9F3EA] rounded-lg"
      fileItem.innerHTML = `
        <div class="flex items-center flex-1 min-w-0">
          <div class="bg-white p-1 rounded mr-3 flex-shrink-0">
            <svg class="w-5 h-5" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M16.3186 1.20795e-05H7.73765C7.32713 -0.0011104 6.92045 0.0760072 6.54121 0.226894C6.16196 0.377782 5.81769 0.599437 5.52834 0.879012L2.58259 3.70701C2.29125 3.98491 2.06029 4.31559 1.90312 4.67986C1.74595 5.04412 1.66568 5.43473 1.66699 5.82901V18C1.65863 18.5215 1.866 19.0248 2.24364 19.3998C2.62127 19.7747 3.13836 19.9905 3.68152 20H16.3186C16.8618 19.9905 17.3789 19.7747 17.7565 19.3998C18.1342 19.0248 18.3415 18.5215 18.3332 18V2.00001C18.3415 1.47854 18.1342 0.975163 17.7565 0.60025C17.3789 0.225336 16.8618 0.00948084 16.3186 1.20795e-05ZM6.87517 2.41401V5.00001H4.1815L6.87517 2.41401ZM3.75026 18V7.00001H6.87517C7.42769 7.00001 7.95758 6.78929 8.34827 6.41422C8.73896 6.03915 8.95845 5.53044 8.95845 5.00001V2.00001L16.2436 1.97801C16.2479 1.98461 16.2501 1.99225 16.2499 2.00001L16.3186 18H3.75026Z" fill="#117D4B"/>
            </svg>
          </div>
          <div class="flex-1 min-w-0">
            <p class="text-sm font-medium text-gray-900 truncate">${file.name}</p>
            <p class="text-xs text-gray-500">${this.formatBytes(file.size)}</p>
          </div>
        </div>
        <button type="button"
                class="ml-3 p-2 text-black hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                data-action="click->document-upload#removeFileFromPreview"
                data-index="${index}">
          <svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      `
      this.previewListTarget.appendChild(fileItem)
    })

    // Show preview container and upload button
    this.previewContainerTarget.classList.remove("hidden")
    if (this.hasUploadButtonTarget) {
      this.uploadButtonTarget.classList.remove("hidden")
    }
  }

  // Remove file from preview
  removeFileFromPreview(event) {
    const index = parseInt(event.currentTarget.dataset.index)
    this.selectedFiles = Array.from(this.selectedFiles).filter((_, i) => i !== index)

    if (this.selectedFiles.length === 0) {
      // Hide preview if no files left
      this.hidePreview()
      // Reset file input
      if (this.hasFileInputTarget) {
        this.fileInputTarget.value = ""
      }
    } else {
      // Refresh preview
      this.showPreview()
    }
  }

  // Hide preview
  hidePreview() {
    if (this.hasPreviewContainerTarget) {
      this.previewContainerTarget.classList.add("hidden")
    }
    if (this.hasUploadButtonTarget) {
      this.uploadButtonTarget.classList.add("hidden")
    }
  }

  // Confirm and upload files
  confirmUpload() {
    if (this.selectedFiles.length === 0) {
      this.showError("Please select at least one file")
      return
    }

    this.uploadFiles(this.selectedFiles)
  }

  // Upload files to server
  async uploadFiles(files) {
    const formData = new FormData()

    // Add files to form data
    files.forEach((file) => {
      formData.append("loan_application[documents][]", file)
    })

    // Add document type
    formData.append("document_type", this.documentTypeValue)

    // Add CSRF token
    const token = document.querySelector('meta[name="csrf-token"]')?.content
    if (token) {
      formData.append("authenticity_token", token)
    }

    // Hide preview and show progress
    this.hidePreview()
    this.showProgress()

    try {
      const response = await this.uploadWithProgress(formData)

      if (response.ok) {
        // Success - reload the page to show updated file list
        window.location.reload()
      } else {
        const errorData = await response.json()
        this.showError(errorData.error || "Upload failed. Please try again.")
      }
    } catch (error) {
      console.error("Upload error:", error)
      this.showError("Upload failed. Please check your connection and try again.")
    } finally {
      this.hideProgress()
    }
  }

  // Upload with progress tracking
  uploadWithProgress(formData) {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest()

      // Track upload progress
      xhr.upload.addEventListener("progress", (event) => {
        if (event.lengthComputable) {
          const percentComplete = (event.loaded / event.total) * 100
          this.updateProgress(percentComplete)
        }
      })

      // Handle completion
      xhr.addEventListener("load", () => {
        resolve(new Response(xhr.response, {
          status: xhr.status,
          statusText: xhr.statusText,
          headers: new Headers({
            'Content-Type': xhr.getResponseHeader('Content-Type')
          })
        }))
      })

      // Handle errors
      xhr.addEventListener("error", () => {
        reject(new Error("Network error"))
      })

      xhr.addEventListener("abort", () => {
        reject(new Error("Upload cancelled"))
      })

      // Build the upload URL - POST to documents endpoint
      const uploadUrl = `/application/${this.applicationIdValue}/documents`

      // Send request
      xhr.open("POST", uploadUrl)
      xhr.send(formData)
    })
  }

  // Delete a file
  async deleteFile(event) {
    event.preventDefault()

    const fileId = event.params.fileId
    const destroyUrl = event.params.destroyUrl

    if (!confirm("Are you sure you want to remove this file?")) {
      return
    }

    try {
      const response = await fetch(destroyUrl, {
        method: "DELETE",
        headers: {
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content,
          "Accept": "application/json"
        }
      })

      if (response.ok) {
        // Remove the file element from DOM
        const fileElement = document.getElementById(`document_${fileId}`)
        if (fileElement) {
          fileElement.remove()
        }

        // Reload page to update file count
        window.location.reload()
      } else {
        const errorData = await response.json()
        this.showError(errorData.error || "Failed to delete file. Please try again.")
      }
    } catch (error) {
      console.error("Delete error:", error)
      this.showError("Failed to delete file. Please check your connection and try again.")
    }
  }

  // UI Helper Methods
  showProgress() {
    if (this.hasProgressContainerTarget) {
      this.progressContainerTarget.classList.remove("hidden")
      this.updateProgress(0)
    }
  }

  hideProgress() {
    if (this.hasProgressContainerTarget) {
      this.progressContainerTarget.classList.add("hidden")
    }
  }

  updateProgress(percent) {
    if (this.hasProgressBarTarget) {
      this.progressBarTarget.style.width = `${percent}%`
    }
    if (this.hasProgressTextTarget) {
      this.progressTextTarget.textContent = `${Math.round(percent)}%`
    }
  }

  showError(message) {
    if (this.hasErrorContainerTarget && this.hasErrorMessageTarget) {
      this.errorMessageTarget.textContent = message
      this.errorContainerTarget.classList.remove("hidden")
    }
  }

  hideError() {
    if (this.hasErrorContainerTarget) {
      this.errorContainerTarget.classList.add("hidden")
    }
  }

  // Format bytes to human-readable size
  formatBytes(bytes, decimals = 2) {
    if (bytes === 0) return "0 Bytes"

    const k = 1024
    const dm = decimals < 0 ? 0 : decimals
    const sizes = ["Bytes", "KB", "MB", "GB"]

    const i = Math.floor(Math.log(bytes) / Math.log(k))

    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + " " + sizes[i]
  }
}
